import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Controller, useForm, type Resolver } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { createUser, listRoles, updateUser, type User } from '@/features/auth/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

/**
 * Create and edit in one dialog, because the two differ in exactly two fields.
 *
 * Email and password appear only on create: the API has no route for changing either on
 * someone else's account, and a password box that quietly does nothing is worse than no
 * box at all. A user changes their own password from their profile; an address change is
 * an identity change and is not in scope for F5.
 */

const baseSchema = z.object({
  full_name: z.string().trim().min(1, 'Enter a name').max(255, 'That name is too long'),
  phone: z.string().trim().max(30, 'That phone number is too long'),
  roles: z.array(z.string()),
  is_active: z.boolean(),
  // Blank is not zero. Blank means "whatever the company rate is"; zero means this person
  // earns nothing, and a rep on a rate the company later raises must not stay behind.
  commission_rate_pct: z
    .string()
    .trim()
    .refine((value) => value === '' || Number.isFinite(Number(value)), {
      message: 'Enter a number, or leave it blank',
    })
    .refine((value) => value === '' || (Number(value) >= 0 && Number(value) <= 100), {
      message: 'A rate is between 0 and 100',
    }),
})

const createSchema = baseSchema.extend({
  email: z.string().min(1, 'Enter an email address').email('That is not a valid email address'),
  password: z
    .string()
    .min(8, 'Use at least 8 characters')
    .refine((value) => new TextEncoder().encode(value).length <= 72, {
      message: 'That is too long — 72 bytes at most',
    }),
})

type FormValues = z.infer<typeof createSchema>

export function UserFormDialog({
  open,
  user,
  onClose,
}: {
  open: boolean
  /** Absent for a new account. */
  user?: User
  onClose: () => void
}) {
  const isEdit = user !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const roles = useQuery({ queryKey: ['roles'], queryFn: listRoles, retry: false })

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    // Edit validates without email and password, which it never shows; the form's own type
    // still carries them, so the narrower resolver is widened to it.
    resolver: (isEdit
      ? zodResolver(baseSchema)
      : zodResolver(createSchema)) as Resolver<FormValues>,
    defaultValues: {
      email: '',
      password: '',
      full_name: user?.full_name ?? '',
      phone: user?.phone ?? '',
      commission_rate_pct: user?.commission_rate_pct ?? '',
      roles: user?.roles.map((role) => role.slug) ?? [],
      is_active: user?.is_active ?? true,
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (user) {
        return updateUser(user.id, {
          full_name: values.full_name,
          phone: values.phone || null,
          is_active: values.is_active,
          roles: values.roles,
          // Sent explicitly, including as null: an absent key would leave a stale override
          // in place on the very save that was meant to clear it.
          commission_rate_pct: values.commission_rate_pct || null,
        })
      }
      return createUser({
        email: values.email,
        password: values.password,
        full_name: values.full_name,
        phone: values.phone || null,
        roles: values.roles,
        commission_rate_pct: values.commission_rate_pct || null,
      })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success(isEdit ? `Saved ${saved.full_name}.` : `Created ${saved.full_name}.`)
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      // 409 is the one worth spelling out: the address is already registered, which the
      // user can fix, unlike anything else that comes back from here.
      setFormError(
        error instanceof ApiError
          ? error.status === 409
            ? 'An account with that email address already exists.'
            : error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${user.full_name}` : 'New user'}
        description={
          isEdit
            ? 'Changes take effect the next time this person signs in.'
            : 'They can sign in as soon as you save.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="user-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Create user'}
            </Button>
          </>
        }
      >
        <form id="user-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          {!isEdit && (
            <>
              <Field label="Email" error={errors.email?.message}>
                {(props) => (
                  <Input
                    {...props}
                    {...register('email')}
                    type="email"
                    autoComplete="off"
                    placeholder="them@company.com"
                  />
                )}
              </Field>

              <Field
                label="Temporary password"
                error={errors.password?.message}
                hint="At least 8 characters. Share it with them directly, not by email."
              >
                {(props) => (
                  <Input
                    {...props}
                    {...register('password')}
                    type="password"
                    autoComplete="new-password"
                  />
                )}
              </Field>
            </>
          )}

          <Field label="Full name" error={errors.full_name?.message}>
            {(props) => <Input {...props} {...register('full_name')} autoComplete="off" />}
          </Field>

          <Field label="Phone" error={errors.phone?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('phone')} type="tel" autoComplete="off" />}
          </Field>

          <Field
            label="Commission rate (%)"
            error={errors.commission_rate_pct?.message}
            hint="Leave blank to use the company rate. 0 means this person earns no commission."
          >
            {(props) => (
              <Input
                {...props}
                {...register('commission_rate_pct')}
                inputMode="decimal"
                autoComplete="off"
                placeholder="Company rate"
                className="tabular-nums"
              />
            )}
          </Field>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Roles
            </legend>
            {roles.error ? (
              <Alert tone="info">
                Roles could not be loaded, so they cannot be changed here right now.
              </Alert>
            ) : (
              <Controller
                control={control}
                name="roles"
                render={({ field }) => (
                  <div className="space-y-2">
                    {(roles.data ?? []).map((role) => (
                      <label key={role.slug} className="flex items-start gap-2 text-sm">
                        <Checkbox
                          className="mt-0.5"
                          checked={field.value.includes(role.slug)}
                          onChange={(event) =>
                            field.onChange(
                              event.target.checked
                                ? [...field.value, role.slug]
                                : field.value.filter((slug) => slug !== role.slug),
                            )
                          }
                        />
                        <span>
                          <span className="font-medium">{role.name}</span>
                          {role.description && (
                            <span className="block text-slate-500 dark:text-slate-400">
                              {role.description}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              />
            )}
          </fieldset>

          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox {...register('is_active')} />
              Active — clearing this blocks sign-in without deleting anything.
            </label>
          )}
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
